import React, { useCallback, useRef } from 'react';
import { useParams } from 'react-router-dom';
import SubPage from '#/common/components/SubPage';
import useAssetTypeQuery from '../hooks/useAssetTypeQuery';
import AssetTypeForm, { SubmitAssetType } from '../components/AssetTypeForm';
import { updateFields } from '../components/AssetTypeForm/fields';
import useAssetTypeUpdate from '../hooks/useUpdateAssetTypeMutation';
import { AssetType } from '#/graphql/types';
import useAssetTypeDelete from '../hooks/useDeleteAssetTypeMutation';
import getFormTitle from '#/common/utils/getFormTitle';
import useNotFoundGuard from '#/common/hooks/useNotFoundGuard';
import RecordNotFound from '#/app/pages/errors/RecordNotFound';

export interface AssetTypeParams {
  readonly id?: string;
}

const AssetTypePage: React.FC = () => {
  const { id } = useParams() as AssetTypeParams;
  const assetTypeQuery = useAssetTypeQuery(id);
  const assetType = assetTypeQuery.data?.getAssetType?.data;
  const isNotFound = useNotFoundGuard({ loading: assetTypeQuery.loading, id, entity: assetType });

  // Use a ref to store the initial updatedAt value to prevent form remount
  // on background refetches (cache-and-network). Only updates after user's own save.
  const formKeyRef = useRef<string | null>(null);
  if (assetType && !formKeyRef.current) {
    formKeyRef.current = assetType.updatedAt ?? assetType.id ?? null;
  }

  const name = assetType?.name;
  const title = getFormTitle(id, name, 'Asset Type');

  const { updateAssetType } = useAssetTypeUpdate();
  const submit = useCallback(
    async (assetType: AssetType) => {
      if (!id) {
        return undefined;
      }

      const result = await updateAssetType(id, assetType);
      // Update the form key after successful save
      if (result?.data?.updatedAt) {
        formKeyRef.current = result.data.updatedAt;
      }
      return result;
    },
    [id, updateAssetType],
  ) as SubmitAssetType;

  const { deleteAssetType: deleteAssetTypeInternal } = useAssetTypeDelete();
  const deleteAssetType = useCallback(() => {
    if (!id) {
      return undefined;
    }

    return deleteAssetTypeInternal(id);
  }, [deleteAssetTypeInternal, id]);

  if (isNotFound) {
    return (
      <SubPage title={title} showGoBackButton type="form" isLoading={false}>
        <RecordNotFound backUrl="/assets/types" />
      </SubPage>
    );
  }

  return (
    <SubPage title={title} isLoading={assetTypeQuery.loading} showGoBackButton type="form">
      {assetType && (
        <AssetTypeForm
          key={`asset-type-${id}-${formKeyRef.current}`}
          assetType={assetType}
          title={title}
          fields={updateFields}
          submit={submit}
          deleteAssetType={deleteAssetType}
        />
      )}
    </SubPage>
  );
};

export default React.memo(AssetTypePage);
